import { GameState } from './GameState.js';
import {
  ALL_DIAGONALS,
  inBounds,
  isPromotionSquare,
  offset,
  type Direction,
} from './path.js';
import type { RuleSet } from './rules/types.js';
import type { Color, Move, Piece, Square } from './types.js';

type Cell = Piece | null;

interface CaptureCandidate {
  readonly over: Square;
  readonly land: Square;
}

/**
 * Enumerates every maximal capture chain available to `color`.
 *
 * Russian shashki specifics encoded here:
 *  - Captures are mandatory at the validator level; this class finds them.
 *  - Captured pieces stay on the board until the chain completes — they act
 *    as blockers and cannot be re-captured (tracked via `capturedIdx`).
 *  - A man landing on the king row mid-chain promotes immediately and the
 *    chain continues with flying-king movement (when `promoteMidJump`).
 *  - Flying kings slide over empty squares to a single opponent, then may
 *    land on any empty square beyond — each landing branches independently.
 *  - The piece is "lifted" from its origin during the search so its own
 *    starting square doesn't block a flying king's slide.
 */
export class CaptureSearch {
  constructor(private readonly ruleSet: RuleSet) {}

  enumerate(state: GameState, color: Color): Move[] {
    const size = this.ruleSet.boardSize;
    const cells = snapshotCells(state, size);
    const out: Move[] = [];
    for (const { square, piece } of state.pieces()) {
      if (piece.color !== color) continue;
      const idx = square.row * size + square.col;
      const origin = cells[idx] ?? null;
      cells[idx] = null;
      this.extend(cells, square, square, piece, false, [], [], new Set(), out);
      cells[idx] = origin;
    }
    return out;
  }

  private extend(
    cells: Cell[],
    from: Square,
    current: Square,
    piece: Piece,
    promotedInChain: boolean,
    path: Square[],
    captures: Square[],
    capturedIdx: Set<number>,
    out: Move[],
  ): void {
    const size = this.ruleSet.boardSize;
    const effectiveKing = piece.kind === 'king' || promotedInChain;
    const directions = this.directionsForCapture(piece, effectiveKing);

    let extended = false;
    for (const dir of directions) {
      const candidates = effectiveKing && this.ruleSet.flyingKings
        ? flyingKingCandidates(cells, current, piece.color, dir, capturedIdx, size)
        : shortCaptureCandidates(cells, current, piece.color, dir, capturedIdx, size);
      for (const { over, land } of candidates) {
        const overIdx = over.row * size + over.col;
        capturedIdx.add(overIdx);
        captures.push(over);
        path.push(land);
        const promote =
          !effectiveKing &&
          this.ruleSet.promoteMidJump &&
          isPromotionSquare(land, piece.color, size);
        this.extend(
          cells,
          from,
          land,
          piece,
          promotedInChain || promote,
          path,
          captures,
          capturedIdx,
          out,
        );
        capturedIdx.delete(overIdx);
        captures.pop();
        path.pop();
        extended = true;
      }
    }

    if (!extended && path.length > 0) {
      const landing = path[path.length - 1];
      if (!landing) return;
      const finishedAsKing =
        piece.kind === 'king' ||
        promotedInChain ||
        isPromotionSquare(landing, piece.color, size);
      out.push({
        from,
        to: landing,
        captures: captures.slice(),
        path: path.slice(),
        promoted: piece.kind === 'man' && finishedAsKing,
      });
    }
  }

  private directionsForCapture(piece: Piece, effectiveKing: boolean): readonly Direction[] {
    if (effectiveKing || this.ruleSet.manCapturesBackward) return ALL_DIAGONALS;
    const dr = piece.color === 'white' ? -1 : 1;
    return ALL_DIAGONALS.filter((d) => d.dr === dr);
  }
}

function snapshotCells(state: GameState, size: number): Cell[] {
  const cells: Cell[] = new Array(size * size).fill(null);
  for (const { square, piece } of state.pieces()) {
    cells[square.row * size + square.col] = piece;
  }
  return cells;
}

function shortCaptureCandidates(
  cells: Cell[],
  from: Square,
  color: Color,
  dir: Direction,
  capturedIdx: Set<number>,
  size: number,
): CaptureCandidate[] {
  const over = offset(from, dir, 1);
  const land = offset(from, dir, 2);
  if (!inBounds(land, size)) return [];
  const overIdx = over.row * size + over.col;
  const overCell = cells[overIdx];
  if (!overCell || overCell.color === color) return [];
  if (capturedIdx.has(overIdx)) return [];
  if (cells[land.row * size + land.col]) return [];
  return [{ over, land }];
}

function flyingKingCandidates(
  cells: Cell[],
  from: Square,
  color: Color,
  dir: Direction,
  capturedIdx: Set<number>,
  size: number,
): CaptureCandidate[] {
  let target: Square | null = null;
  for (let d = 1; ; d += 1) {
    const sq = offset(from, dir, d);
    if (!inBounds(sq, size)) return [];
    const idx = sq.row * size + sq.col;
    const cell = cells[idx];
    if (!cell) continue;
    if (cell.color === color || capturedIdx.has(idx)) return [];
    target = sq;
    break;
  }
  const results: CaptureCandidate[] = [];
  for (let d = 1; ; d += 1) {
    const land = offset(target, dir, d);
    if (!inBounds(land, size)) break;
    if (cells[land.row * size + land.col]) break;
    results.push({ over: target, land });
  }
  return results;
}
